import React, { useState, useEffect } from 'react';
import { Modal, Card, Typography, Space, Button, Divider, Row, Col, Descriptions, App } from 'antd';
import {
  PrinterOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CloseOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { Table } from '../../../types/tables';
import { Order } from '../../../types/orders';
import { useGetOrderDiscountsQuery } from '../../../services/ordersApi';
import { roundCents, withDiscountLabels } from '../../../utils/discounts';
import { useResponsive } from '../../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import { useGetRestaurantSettingsQuery } from '../../../services/restaurantsApi';
import { printAgentService } from '../../../services/printAgentService';
import { buildOrderTicketPreview, withPrintAnimation } from '../../../services/printAnimation';
import { shouldUseCloudPrint } from '../../../utils/cloudPrintRoute';
import { bluetoothPrintService } from '../../../services/bluetoothPrintService';
import { useGetFacturacionConfigQuery } from '../../../services/facturacionApi';
import { useGetBoolSettingQuery } from '../../../services/settingsApi';
import { useCloudPrint } from '../../../hooks/useCloudPrint';
import { useGetOrderSplitsQuery } from '../../../services/splitsApi';
import { OrderSplit } from '../../../types/splits';
import { buildSplitOrder, splitChargedAmount, splitItemsSummary } from '../../../utils/splitOrder';
import SplitCreator from '../../splits/SplitCreator';
import { formatOrderNumber } from '../../../utils/orderNumber';

const { Title, Text } = Typography;

interface PreCheckModalProps {
  visible: boolean;
  onCancel: () => void;
  table?: Table;
  order?: Order;
  /**
   * Qué persistir antes de imprimir. Desde el menú de mesa es pasar la mesa a estado
   * Precuenta; desde la lista de órdenes (para llevar/domicilio) no hay nada que
   * persistir —no hay mesa que cambiar de estado— y se omite: la precuenta es solo el
   * papel. Si falla, no se imprime.
   *
   * No recibe (table, order): el modal ya los tiene por props y devolvérselos al padre
   * obligaba a que existiera una mesa.
   */
  onPrintAndSave?: () => Promise<void>;
}

const PreCheckModal: React.FC<PreCheckModalProps> = ({
  visible,
  onCancel,
  table,
  order,
  onPrintAndSave,
}) => {
  const { message } = App.useApp();
  const [printing, setPrinting] = useState(false);
  const [creatorVisible, setCreatorVisible] = useState(false);
  const [, setPrintAgentAvailable] = useState(false);
  const [, setBluetoothAvailable] = useState(false);
  const { isMobile, isTablet } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Get restaurant settings for tax rate
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  // Get facturacion config for autofactura URL on tickets
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  // Whether the autofactura QR should be printed on precuenta tickets (default true)
  const { data: qrPrecuentaSetting } = useGetBoolSettingQuery('AutofacturaQr_Precuenta');
  const qrEnPrecuenta = qrPrecuentaSetting?.value ?? true;
  const taxRate = (restaurantSettings?.taxRate || 16) / 100; // Convert percentage to decimal

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();

  // Check which print services are available on mount
  useEffect(() => {
    const checkPrintServices = async () => {
      // Check Web Bluetooth API (Android/Chrome)
      const btAvailable = bluetoothPrintService.isAvailable();
      setBluetoothAvailable(btAvailable);

      // Check Print Agent (Windows)
      const agentAvailable = await printAgentService.isAvailable();
      setPrintAgentAvailable(agentAvailable);
    };

    if (visible) {
      checkPrintServices();
    }
  }, [visible]);

  // Get discounts for this order (always call hook, but skip if no order)
  const {
    data: orderDiscounts
  } = useGetOrderDiscountsQuery(order?.id || '', {
    skip: !order?.id
  });

  // Cuentas ya armadas. La precuenta es donde el cliente ve cómo salieron, así
  // que si la orden está dividida imprime un ticket por cuenta en vez de un
  // total que no le toca pagar a nadie.
  const {
    data: splits,
    isError: splitsFailed,
    refetch: refetchSplits,
  } = useGetOrderSplitsQuery(order?.id || '', {
    skip: !order?.id || !visible || !order?.isSplit,
    refetchOnMountOrArgChange: true,
  });

  if (!order) return null;

  const cuentas: OrderSplit[] = order.isSplit ? splits ?? [] : [];
  const isSplitOrder = cuentas.length > 0;
  // Con la orden dividida y las cuentas sin llegar todavía (o con la consulta
  // caída) NO se puede imprimir: caer al camino de un solo ticket sacaría un
  // total que no va a pagar nadie, con su QR de autofactura por la orden
  // completa. Mejor esperar/reintentar que imprimir la cuenta equivocada.
  const cuentasPendientes = !!order.isSplit && cuentas.length === 0;
  // Con comensales capturados, dividir es un camino esperado; sin ellos el
  // mesero tendría que armarlas a mano y el botón solo estorbaría aquí.
  const hasSeatedItems = (order.orderItems || []).some(i => !i.isCancelled && i.seatNumber);

  // El prop `table` solo llega desde el menú de mesas. Para llevar y domicilio no tienen
  // mesa real (viven en mesas virtuales que el grid ni siquiera lista), así que la cuenta
  // se identifica con los datos de la orden. Mismo criterio que PaymentModal.
  const orderTypeLabel = order.type === 'Delivery' ? 'Domicilio' : 'Para Llevar';
  const displayLabel = table
    ? `Mesa ${table.number}${table.areaName ? ` - ${table.areaName}` : ''}`
    : order.type === 'DineIn'
      ? (order.tableName ? `Mesa ${order.tableName}` : 'Mesa')
      : orderTypeLabel;
  // Lo que va impreso en el encabezado del ticket, donde normalmente va la mesa.
  const ticketTableName = table
    ? `Mesa ${table.number}`
    : order.type === 'DineIn'
      ? (order.tableName ? `Mesa ${order.tableName}` : 'Mesa')
      : orderTypeLabel;

  // Get real data from order
  const assignedWaiter = order.assignedWaiterName;
  const openTime = order.openTime ? formatInTimeZone(order.openTime, 'HH:mm') : undefined;

  // Calculate totals from order
  const orderItems = order.orderItems || [];
  const activeItems = orderItems.filter(item => !item.isCancelled);

  // NOTE: Total price is used as base for tax calculation
  // Tax is calculated as a percentage of the total, then subtracted to get subtotal
  const totalWithTax = activeItems.reduce((sum, item) => {
    return sum + item.total; // item.total already includes variants and IVA
  }, 0);

  // Extract tax from total (prices already include IVA)
  // Subtotal = Total / (1 + taxRate), Tax = Total - Subtotal
  const subtotal = totalWithTax / (1 + taxRate);
  const tax = totalWithTax - subtotal;
  const totalBeforeDiscount = totalWithTax;

  // Calculate discount amount from applied discounts
  // Los de producto salen como "Cortesía (Café)" en pantalla y en el ticket
  const appliedDiscounts = withDiscountLabels(orderDiscounts || [], orderItems);
  const totalDiscountAmount = appliedDiscounts.reduce((sum, discount) => sum + discount.discountAmount, 0);
  
  const total = Math.max(roundCents(totalBeforeDiscount - totalDiscountAmount), 0);
  // Comensales capturados al abrir la mesa (antes estaba fijo en 5)
  const comensales = (order.adults || 0) + (order.children || 0);

  // Calculate if we need flexible height based on number of discounts
  const needsFlexibleHeight = appliedDiscounts.length > 1;

  /**
   * Un ticket. La orden dividida imprime uno por cuenta llamando a esto en
   * bucle con la orden sintética de cada una: los servicios de impresión leen
   * los renglones de `orderToPrint` y no necesitan saber qué es una cuenta.
   */
  const printOneTicket = async (
    orderToPrint: Order,
    ticketLabel: string,
    amounts: { subtotal: number; tax: number; total: number },
    discounts: { id?: string; reason?: string; discountAmount: number }[],
    autofacturaCuenta?: number,
    services?: { agentReady: boolean; btReady: boolean }
  ) => {
    const agentReady = services?.agentReady ?? (await printAgentService.isAvailable());
    const btReady = services?.btReady ?? bluetoothPrintService.isAvailable();

    // El total del QR es el de ESTE ticket: con una orden dividida, el total de
    // la orden completa no coincide con lo que va a pagar nadie y la validación
    // de autofactura lo rechazaría. &cuenta identifica la cuenta. Sin QR en una orden
    // tomada sin conexión: su número es 0 hasta sincronizar y no se puede facturar.
    const autofacturaUrl = qrEnPrecuenta && facturacionConfig?.autofacturaEnabled && facturacionConfig?.autofacturaCode
      && !order.offlineFolioLabel
      ? `${window.location.origin}/autofactura/${facturacionConfig.autofacturaCode}?order=${order.orderNumber}&total=${amounts.total.toFixed(2)}${autofacturaCuenta ? `&cuenta=${autofacturaCuenta}` : ''}`
      : undefined;

    const printableItems = (orderToPrint.orderItems || []).filter(i => !i.isCancelled);

    // Lo que dibuja la animación de impresión (si el restaurante la activó).
    const preview = buildOrderTicketPreview({
      kind: 'precuenta',
      title: 'PRECUENTA',
      tableLabel: ticketLabel,
      waiterName: order.assignedWaiterName || undefined,
      orderLabel: formatOrderNumber(order),
      items: printableItems,
      subtotal: amounts.subtotal,
      tax: amounts.tax,
      discounts,
      total: amounts.total,
    });

    if (shouldUseCloudPrint(cloudPrintSettingOn, order)) {
      await withPrintAnimation(preview, () => cloudPrintJob({
        printType: 'PreCheck',
        // Field names must match the backend EscPosReceiptData DTO exactly
        // (JSON binding is case-insensitive but does not translate names).
        // DateTime is intentionally omitted: the backend stamps it in the
        // restaurant's timezone. See CloudPrintJobService.EnqueueAsync.
        receiptData: {
          orderNumber: order.orderNumber,
          restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
          address: restaurantSettings?.address,
          phone: restaurantSettings?.phone,
          title: 'PRE-CUENTA',
          tableName: ticketLabel,
          waiterName: order.assignedWaiterName || 'Sin asignar',
          items: printableItems.map(item => ({
            name: item.productName,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.total,
            variants: item.variants?.map(v => v.variantName) || [],
          })),
          subtotal: amounts.subtotal,
          tax: amounts.tax,
          taxRate,
          discounts: discounts.map(d => ({ reason: d.reason || 'Descuento', amount: d.discountAmount })),
          total: amounts.total,
          autofacturaUrl,
        },
      }));
      return { success: true, message: '' };
    }

    if (agentReady) {
      return withPrintAnimation(preview, () => printAgentService.printPreCheck(
        orderToPrint,
        restaurantSettings?.name || 'MASSRESTAURANT',
        restaurantSettings?.address,
        restaurantSettings?.phone,
        ticketLabel,
        order.assignedWaiterName || 'Sin asignar',
        amounts.subtotal,
        amounts.tax,
        taxRate,
        discounts as any,
        amounts.total,
        autofacturaUrl
      ));
    }

    if (btReady) {
      return withPrintAnimation(preview, () => bluetoothPrintService.printPreCheck(
        orderToPrint,
        restaurantSettings?.name || 'MASSRESTAURANT',
        restaurantSettings?.address,
        restaurantSettings?.phone,
        ticketLabel,
        order.assignedWaiterName || 'Sin asignar',
        amounts.subtotal,
        amounts.tax,
        taxRate,
        discounts as any,
        amounts.total,
        autofacturaUrl
      ));
    }

    console.warn('No print service available');
    return null;
  };

  // Unified print function with priority-based service selection
  const handlePrintAndSave = async () => {
    if (!order) return;
    // Con la consulta caída el botón reintenta en vez de quedarse muerto:
    // dejar a quien cobra sin salida ya nos costó un PR (#147) en el modal de
    // división.
    if (cuentasPendientes) {
      if (splitsFailed) {
        refetchSplits();
        message.warning('Reintentando cargar las cuentas de esta orden…');
      } else {
        message.warning('Cargando las cuentas de esta orden…');
      }
      return;
    }

    setPrinting(true);
    try {
      // Re-check print services at print time to avoid race condition
      const agentReady = await printAgentService.isAvailable();
      const btReady = bluetoothPrintService.isAvailable();
      const services = { agentReady, btReady };
      // First, save and change table status (this is critical)
      await onPrintAndSave?.();

      // Then try to print (don't block on failure)
      try {
        if (isSplitOrder) {
          // Un ticket por cuenta, en orden. Si una impresión falla se sigue con
          // las demás: dejar la mesa con la mitad de los tickets es peor que
          // avisar al final cuáles faltaron.
          const failed: string[] = [];
          for (const split of cuentas) {
            const charged = splitChargedAmount(split);
            // El try va DENTRO del bucle: si la impresión de una cuenta revienta
            // (Cloud Print sin red, el agente caído) las demás tienen que salir
            // igual. Con el try por fuera, un fallo en la cuenta 2 dejaba las 3
            // siguientes sin ticket y avisaba "procesada" como si nada.
            try {
              const result = await printOneTicket(
                buildSplitOrder(order, split),
                `${ticketTableName} · ${split.name}`,
                { subtotal: split.subtotal, tax: split.tax, total: charged },
                split.splitDiscounts?.map(d => ({ reason: d.reason, discountAmount: d.discountAmount })) || [],
                split.splitNumber,
                services
              );
              if (result && !result.success) failed.push(split.name);
            } catch (splitPrintError) {
              console.warn(`Print failed for ${split.name}:`, splitPrintError);
              failed.push(split.name);
            }
          }
          if (failed.length === 0) {
            message.success(`Precuenta impresa: ${cuentas.length} cuentas`);
          } else {
            message.warning(`No se pudieron imprimir: ${failed.join(', ')}`);
          }
        } else {
          const result = await printOneTicket(
            order,
            ticketTableName,
            { subtotal, tax, total },
            appliedDiscounts,
            undefined,
            services
          );
          if (!result) {
            message.success('Precuenta procesada (impresión no disponible)');
          } else if (result.success) {
            message.success('Precuenta procesada e impresa correctamente');
          } else {
            message.warning('Precuenta procesada pero falló la impresión: ' + result.message);
          }
        }
      } catch (printError: any) {
        // Log print error but continue anyway
        console.warn('Print failed (continuing anyway):', printError);
        message.success('Precuenta procesada (impresión no disponible)');
      }

      onCancel();
    } catch (error: any) {
      // Only catch errors from onPrintAndSave
      message.error('Error al procesar precuenta: ' + (error.message || 'Error desconocido'));
      console.error('Save error:', error);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <Modal
        title={null}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={isMobile ? '100vw' : isTablet ? '90vw' : 600}
        style={{
          top: isMobile ? 0 : 20,
          maxWidth: isMobile ? '100vw' : '600px',
          height: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          body: {
            padding: 0,
            height: isMobile ? '100vh' : 'auto',
            overflowY: isMobile ? 'auto' : 'visible'
          }
        }}
        destroyOnClose
        centered={!isMobile}
      >
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: isMobile ? '16px' : '24px',
          borderBottom: '2px solid #faad14',
          paddingBottom: isMobile ? '12px' : '16px'
        }}>
          <Title level={isMobile ? 3 : 2} style={{ marginBottom: '8px', color: '#faad14' }}>
            <PrinterOutlined style={{ marginRight: '8px' }} />
            {isMobile ? 'PRECUENTA' : 'PRECUENTA'}
          </Title>
          <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
            {displayLabel} · Orden {formatOrderNumber(order)}
          </Text>
        </div>

        <Row gutter={isMobile ? [0, 16] : [24, 0]}>
          {/* Left Column - Table Info */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {table
                      ? (isMobile ? 'Info Mesa' : 'Información de Mesa')
                      : (isMobile ? 'Info Orden' : 'Información de la Orden')}
                  </span>
                </Space>
              }
              size={isMobile ? "small" : "small"}
              style={{ 
                height: isMobile ? 'auto' : needsFlexibleHeight ? 'auto' : '280px',
                marginBottom: isMobile ? '12px' : '0',
                minHeight: isMobile ? 'auto' : '280px'
              }}
            >
              <Descriptions 
                column={1} 
                size={isMobile ? "small" : "small"}
                labelStyle={{ fontSize: isMobile ? '12px' : '14px' }}
                contentStyle={{ fontSize: isMobile ? '12px' : '14px' }}
              >
                {table ? (
                  <Descriptions.Item label="Mesa">
                    {table.number} - {table.areaName}
                  </Descriptions.Item>
                ) : (
                  <Descriptions.Item label="Tipo">
                    {orderTypeLabel}
                  </Descriptions.Item>
                )}
                {table && (
                  <Descriptions.Item label="Capacidad">
                    {table.capacity} personas
                  </Descriptions.Item>
                )}
                {/* Para llevar y domicilio: los datos del cliente ocupan el lugar que en
                    una mesa ocupan mesa y capacidad. Sin esto la tarjeta quedaba vacía. */}
                {!table && order.customerName && (
                  <Descriptions.Item label="Cliente">
                    {order.customerName}
                  </Descriptions.Item>
                )}
                {!table && order.customerPhone && (
                  <Descriptions.Item label="Teléfono">
                    {order.customerPhone}
                  </Descriptions.Item>
                )}
                {!table && order.deliveryAddress && (
                  <Descriptions.Item label="Dirección">
                    <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{order.deliveryAddress}</span>
                  </Descriptions.Item>
                )}
                {!table && order.deliveryNotes && (
                  <Descriptions.Item label="Indicaciones">
                    <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{order.deliveryNotes}</span>
                  </Descriptions.Item>
                )}
                {comensales > 0 && (
                  <Descriptions.Item label="Comensales">
                    {comensales} personas
                  </Descriptions.Item>
                )}
                {assignedWaiter && (
                  <Descriptions.Item label="Mesero">
                    <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{assignedWaiter}</span>
                  </Descriptions.Item>
                )}
                {openTime && (
                  <Descriptions.Item label="Hora de Apertura">
                    <Space size="small">
                      <ClockCircleOutlined />
                      <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{openTime}</span>
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>

          {/* Right Column - Order Summary */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <DollarOutlined />
                  <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {isMobile ? 'Totales' : 'Resumen de Cuenta'}
                  </span>
                </Space>
              }
              size={isMobile ? "small" : "small"}
              style={{ 
                height: isMobile ? 'auto' : needsFlexibleHeight ? 'auto' : '280px',
                marginBottom: isMobile ? '12px' : '0',
                minHeight: isMobile ? 'auto' : '280px'
              }}
            >
              <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                {/* Orden dividida: manda el desglose por cuenta, que es lo que
                    se va a imprimir y lo que cada quien va a pagar. */}
                {isSplitOrder && (
                  <div style={{ marginBottom: 12 }}>
                    {cuentas.map(split => (
                      <div key={split.id} style={{ marginBottom: 8 }}>
                        <Row justify="space-between">
                          <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{split.name}</Text>
                          <Text strong style={{ fontSize: isMobile ? '13px' : '14px', fontVariantNumeric: 'tabular-nums' }}>
                            ${splitChargedAmount(split).toFixed(2)}
                          </Text>
                        </Row>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {splitItemsSummary(split)}
                        </Text>
                      </div>
                    ))}
                    <Divider style={{ margin: isMobile ? '6px 0' : '8px 0' }} />
                  </div>
                )}
                <Row justify="space-between" style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Productos:</Text>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>{activeItems.length} items</Text>
                </Row>
                <Row justify="space-between" style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Subtotal:</Text>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>${subtotal.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between" style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>IVA ({(taxRate * 100).toFixed(0)}%):</Text>
                  <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>${tax.toFixed(2)}</Text>
                </Row>
                
                {/* Show applied discounts */}
                {appliedDiscounts.length > 0 && (
                  <>
                    {appliedDiscounts.map((discount, index) => (
                      <Row key={discount.id} justify="space-between" style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                        <Text style={{ 
                          color: '#ff4d4f', 
                          fontSize: isMobile ? '12px' : '14px' 
                        }}>
                          Descuento {index + 1} ({discount.reason}):
                        </Text>
                        <Text style={{ 
                          color: '#ff4d4f', 
                          fontSize: isMobile ? '12px' : '14px' 
                        }}>
                          -${discount.discountAmount.toFixed(2)}
                        </Text>
                      </Row>
                    ))}
                  </>
                )}
                
                <Divider style={{ margin: isMobile ? '6px 0' : '8px 0' }} />
                <Row justify="space-between">
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Total:</Text>
                  <Text strong style={{ 
                    fontSize: isMobile ? '14px' : '16px', 
                    color: '#faad14' 
                  }}>
                    ${total.toFixed(2)}
                  </Text>
                </Row>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Actions Row - Full Width */}
        <Row style={{ marginTop: isMobile ? '16px' : '24px' }}>
          <Col span={24}>
            <Card 
              title={
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                  Acciones
                </span>
              }
              size="small"
            >
              <Row gutter={isMobile ? [8, 8] : [16, 0]}>
                {/* Unified Print Button - automatically uses Print Agent or Bluetooth */}
                <Col xs={24} sm={16}>
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    size={isMobile ? "middle" : "large"}
                    block
                    onClick={handlePrintAndSave}
                    loading={printing || (cuentasPendientes && !splitsFailed)}
                    // Deshabilitado solo mientras cargan; si la consulta falló,
                    // el botón sigue vivo para reintentar.
                    disabled={cuentasPendientes && !splitsFailed}
                    style={{
                      backgroundColor: '#faad14',
                      borderColor: '#faad14',
                      transition: 'all 0.3s ease',
                      marginBottom: isMobile ? '8px' : '0'
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.backgroundColor = '#e8940a';
                        e.currentTarget.style.borderColor = '#e8940a';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px var(--shadow-color-strong)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.backgroundColor = '#faad14';
                        e.currentTarget.style.borderColor = '#faad14';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {cuentasPendientes
                      ? (splitsFailed ? 'Reintentar cargar cuentas' : 'Cargando cuentas…')
                      : isSplitOrder
                        ? `Imprimir ${cuentas.length} cuentas`
                        : isMobile ? 'Imprimir' : 'Imprimir Ticket'}
                  </Button>
                </Col>

                {/* Cancel */}
                <Col xs={24} sm={8}>
                  <Button
                    icon={<CloseOutlined />}
                    size={isMobile ? "middle" : "large"}
                    block
                    onClick={onCancel}
                    danger
                  >
                    Cancelar
                  </Button>
                </Col>
              </Row>

              {/* Separar la cuenta ANTES de imprimir: es aquí donde el cliente
                  ve cómo salió cada quien, y lo que se arme queda listo para
                  cobrar. Solo se ofrece con comensales capturados; sin ellos
                  hay que armarlas a mano y el camino vive en "Dividir cuenta". */}
              {!isSplitOrder && hasSeatedItems && (
                <Row style={{ marginTop: 12 }}>
                  <Col span={24}>
                    <Button
                      type="default"
                      icon={<TeamOutlined />}
                      size={isMobile ? 'middle' : 'large'}
                      block
                      onClick={() => setCreatorVisible(true)}
                    >
                      Imprimir cuentas separadas
                    </Button>
                  </Col>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </Modal>

    <SplitCreator
      visible={creatorVisible}
      orderId={order.id}
      orderData={order}
      onComplete={() => setCreatorVisible(false)}
      onCancel={() => setCreatorVisible(false)}
    />
    </>
  );
};

export default PreCheckModal;